import React, { useRef, useState, useEffect } from 'react';
import { Tooltip } from 'antd';

interface AutoTooltipProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    tooltipPlacement?: 'topLeft' | 'top' | 'topRight' | 'bottomLeft' | 'bottom' | 'bottomRight';
    className?: string;
    style?: React.CSSProperties;
    showTitle?: boolean; // Use native browser tooltip instead of Ant Design tooltip
}

export const AutoTooltip = ({
    children,
    tooltipPlacement = 'topLeft',
    className = '',
    style = {},
    showTitle = true,
    ...props
}: AutoTooltipProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipContent, setTooltipContent] = useState<string>('');

    const checkOverflow = () => {
        const el = contentRef.current;
        if (!el) return;

        const isOverflowing = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
        setShowTooltip(isOverflowing);

        if (isOverflowing) {
            setTooltipContent(el.textContent?.trim() || '');
        }
    };

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [children]);

    const content = (
        <div
            style={style}
            ref={contentRef}
            className={`truncate ${className}`}
            title={showTitle && showTooltip ? tooltipContent : undefined}
            onMouseEnter={checkOverflow}
            {...props}
        >
            {children}
        </div>
    );

    // If showTitle is true, use native browser tooltip (no wrapper)
    if (showTitle) {
        return content;
    }

    // Otherwise use Ant Design Tooltip
    return (
        <Tooltip
            title={tooltipContent}
            placement={tooltipPlacement}
            open={showTooltip ? undefined : false} // Prevents flickering
        >
            {content}
        </Tooltip>
    );
};

export default AutoTooltip;
